/**
 * What the user can be told about the translations of the active language (#4114).
 *
 * `failure-resilience.ts` writes into it (a namespace failed, a namespace recovered, a retry is running); the
 * notice of `components/TranslationsDegradedNotice.tsx` reads it through `useSyncExternalStore`, so the phase is
 * a plain string (stable between calls) and the subscription is the only thing that triggers a render.
 *
 * The phase is always about the ACTIVE language: a namespace of `en` that failed while `es` is shown changes
 * nothing for the person looking at the screen.
 */

import type { i18n as I18nInstance } from 'i18next'

import { logger } from '@/shared/utils/logger'

type TranslationLoadPhase = 'ok' | 'degraded' | 'retrying' | 'still-failing'

export interface TranslationLoadStatus {
  getSnapshot(): TranslationLoadPhase
  subscribe(listener: () => void): () => void
  /** Ask the server again for every namespace of the active language that is missing. */
  retry(): Promise<void>
}

/** The write side, used by the failure handling only. */
export interface TranslationLoadStatusWriter {
  /** Returns the function that unbinds the instance again. */
  bind(instance: I18nInstance, retry: () => Promise<boolean>): () => void
  markFailed(language: string, namespace: string): void
  markLoaded(language: string, namespace: string): void
  /** A background retry is in flight. */
  setRetrying(on: boolean): void
}

const keyOf = (language: string, namespace: string): string => `${language}|${namespace}`

/** `es-ES` is served by the bundle of `es`, so a failure of `es` is a failure of the active `es-ES`. */
const isActiveLanguage = (active: string, failedLanguage: string): boolean =>
  active === failedLanguage || active.startsWith(`${failedLanguage}-`)

export const createTranslationLoadStatus = (): TranslationLoadStatus &
  TranslationLoadStatusWriter => {
  const failed = new Set<string>()
  const listeners = new Set<() => void>()
  let instance: I18nInstance | null = null
  let retryAll: (() => Promise<boolean>) | null = null
  let backgroundRetrying = false
  let manualRetrying = false
  let manualRetryFailed = false
  let phase: TranslationLoadPhase = 'ok'

  const compute = (): TranslationLoadPhase => {
    const active = instance?.language
    const hasFailed =
      !!active &&
      [...failed].some((key) => isActiveLanguage(active, key.slice(0, key.indexOf('|'))))
    if (!hasFailed) return 'ok'
    if (backgroundRetrying || manualRetrying) return 'retrying'
    return manualRetryFailed ? 'still-failing' : 'degraded'
  }

  const refresh = (): void => {
    const next = compute()
    if (next === 'ok') manualRetryFailed = false
    if (next === phase) return
    phase = next
    for (const listener of [...listeners]) listener()
  }

  return {
    getSnapshot: () => phase,

    subscribe(listener) {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },

    async retry() {
      if (!retryAll) {
        logger.error('[i18n] A translation retry was requested before the failure handling existed')
        return
      }
      if (backgroundRetrying || manualRetrying) return
      manualRetrying = true
      refresh()
      let recovered = false
      try {
        recovered = await retryAll()
      } catch (error) {
        logger.error('[i18n] The manual translation retry failed', {
          message: error instanceof Error ? error.message : String(error),
        })
      } finally {
        manualRetrying = false
        manualRetryFailed = !recovered
        refresh()
      }
    },

    bind(target, retry) {
      instance = target
      retryAll = retry
      const onLanguageChanged = (): void => refresh()
      target.on('languageChanged', onLanguageChanged)
      return () => {
        target.off('languageChanged', onLanguageChanged)
        if (instance === target) {
          instance = null
          retryAll = null
          failed.clear()
          backgroundRetrying = false
          manualRetrying = false
          refresh()
        }
      }
    },

    markFailed(language, namespace) {
      failed.add(keyOf(language, namespace))
      refresh()
    },

    markLoaded(language, namespace) {
      if (failed.delete(keyOf(language, namespace))) refresh()
    },

    setRetrying(on) {
      backgroundRetrying = on
      refresh()
    },
  }
}

/** The status of the application's i18n instance. */
export const appTranslationLoadStatus = createTranslationLoadStatus()
